/**
 * Compressão de fotos no cliente (canteiro: 4G fraco, IndexedDB finito).
 * Redimensiona para `maxPx` no maior lado e reencoda em JPEG.
 * Falha silenciosa: devolve o arquivo original se o browser não suportar canvas.
 */
export interface CompressaoOpcoes {
  maxPx?: number
  qualidade?: number
}

export async function comprimirImagem(
  file: File | Blob,
  { maxPx = 1600, qualidade = 0.82 }: CompressaoOpcoes = {},
): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file
  try {
    const bitmap = await carregarBitmap(file)
    const escala = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height))
    const w = Math.max(1, Math.round(bitmap.width * escala))
    const h = Math.max(1, Math.round(bitmap.height * escala))

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, w, h)
    if ('close' in bitmap) bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', qualidade),
    )
    if (!blob) return file
    // Se a compressão não ajudou (foto já pequena), mantém original.
    return blob.size < file.size ? blob : file
  } catch {
    return file
  }
}

async function carregarBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      /* fallback abaixo */
    }
  }
  const url = URL.createObjectURL(file)
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('Imagem inválida'))
      img.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function nomeFotoObra(data = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `foto-${data.getFullYear()}${pad(data.getMonth() + 1)}${pad(data.getDate())}-${pad(data.getHours())}${pad(data.getMinutes())}${pad(data.getSeconds())}.jpg`
}
